<template>
  <div>
    <es-loader
      v-if="loading || loadingSavedData"
      style="margin-top: 3rem"
    />
    <template v-else>
    <es-form>
      <template #es-form>
        <div class="row removeUnderline">
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">
                Person Type
              </label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control">
                  <div class="buttons has-addons">
                    <button
                      v-for="(option, index) in sortedPersonTypesOptions"
                      :key="index"
                      class="button button--form"
                      :class="{'is-active': personType === option.code}"
                      @click="personType = option.code"
                      :disabled="!enabledSubmissionStatus
                        .includes(option.code) && option.code !== null"
                      style="min-width: 100px"
                    >
                      {{ option.name }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row removeUnderline">
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">
                ID Type
              </label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control">
                  <div class="buttons has-addons">
                    <button
                      v-for="(idType, index) in idTypesDisplayed"
                      :key="index"
                      class="button button--form"
                      :class="{'is-active': selectedIdType === idType.code}"
                      @click="onIdTypeChange(idType.code)"
                      style="min-width: 100px"
                    >
                      {{ idType.description }}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="row removeUnderline">
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label" for="identifierID">
                Identifier
              </label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control">
                  <input
                    class="input"
                    :class="{'is-danger' : hasErrorIdentifier}"
                    @keypress="checkSsnInput($event)"
                    @keyup.prevent="validateField('identifier')"
                    @paste="onPasteValidation('identifier')"
                    type="text"
                    v-model="id"
                    id="identifierID"
                    :placeholder="selectedIdType === 'ssn' ? '###-##-#### OR #########' : ''"
                  >
                  <span v-if ="hasErrorIdentifier" class="help is-danger">
                    {{ msgIdentifier }}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div
          v-if="selectedIdType === 'localId'"
          class="row removeUnderline"
        >
          <div class="field is-horizontal">
            <div class="field-label is-normal">
              <label class="label">
                <span v-if="personType === null">
                  {{ getFieldLabel(`PER:${defaultPersonType}:SEARCH:SOURCE_CODE`) }}
                </span>
                <span v-else>
                  {{ getFieldLabel(`PER:${personType}:SEARCH:SOURCE_CODE`) }}
                </span>
              </label>
            </div>
            <div class="field-body">
              <div class="field">
                <div class="control">
                  <es-select
                    v-on:update:values="sources = $event; validateField('source')"
                    valueTpl="${description}"
                    :setValues="activeSources"
                    :preSelected="sources"
                    ref="selSource"
                    keyValue="code"
                    v-bind:clearOut="true"
                    :hasError="hasErrorSource"
                    :errorMsg="msgSource"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </es-form>
    <div
      class="is-flex flex-justify--end flex-align--center"
      style="margin-right: 5.15rem; padding-bottom: 2rem"
    >
      <button
        class="button button-link has-underline"
        @click="reset"
        style="margin-right: 1rem"
      >
        Reset
      </button>
      <button
        class="button is-primary"
        @click="search"
        :disabled="loadingStudents"
      >
        Search
      </button>
    </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import { library } from '@fortawesome/fontawesome-svg-core';
import { mapGetters, mapActions } from 'vuex';
import {
  faUser,
} from '@fortawesome/pro-solid-svg-icons';
import EsForm from '@/components/EsForm.vue';
import EsLoader from '@/components/EsLoader.vue';
import EsSelect from '@/components/EsSelect.vue';
import FieldLabels from '@/mixins/FieldLabels';
import PermissionManager from '@/mixins/PermissionManager';
import NotificationManager from '@/mixins/NotificationManager';

library.add(
  faUser,
);

export default {
  mixins: [
    FieldLabels,
    PermissionManager,
    NotificationManager,
  ],
  components: {
    EsForm,
    EsLoader,
    EsSelect,
  },
  props: {
    sortField: {
      type: String,
      default: 'name',
    },
    sortOrder: {
      type: String,
      default: 'ASC',
    },
    loadingStudents: {
      type: Boolean,
    },
  },
  data() {
    return {
      allSubmissionSpecs: [],
      hasErrorIdentifier: false,
      hasErrorSource: false,
      loading: false,
      loadingSavedData: false,
      loadingError: false,
      idTypes: [],
      id: null,
      msgIdentifier: null,
      msgSource: null,
      personType: null,
      selectedIdType: 'statePersonId',
      sources: [],
      ssnPermission: 'PRSID:SRCH:OPTIONS:SSN:SRCH',
      studentInfoRaw: [],
      studentInfoCleaned: [],
      userPersonTypesCodes: null,
    };
  },
  watch: {
    personType() {
      this.updateIdTypes();
    },
    selectedIdType() {
      this.hasErrorIdentifier = false;
      this.msgIdentifier = null;
      this.resetNotifications();
    },
    '$route.query.page': {
      handler(newPage) {
        const saved = this.$store.getters['personSearchModule/getSearchByType']('searchById');
        if (saved && Object.keys(saved).length > 0) {
          this.$store.dispatch('personSearchModule/setSearchState', {
            searchType: 'searchById',
            ...saved,
            pageNum: newPage ? Number(newPage) : null,
          });
        }
      },
    },
  },
  created() {
    // await this.loadPersonTypes();
    this.setDataElements();
  },
  async mounted() {
    this.loading = true;
    this.loadingError = false;
    try {
      await Promise.all([
        this.loadSources(),
        this.loadPersonTypes(),
        this.loadSources(),
      ]);
      this.sourceInput = this.defaultSource;
      if (this.userPersonTypesCodes && this.userPersonTypesCodes.length > 0) {
        this.personType = this.userPersonTypesCodes.includes(this.defaultPersonType)
          ? this.defaultPersonType : this.userPersonTypesCodes[0];
      } else {
        this.personType = null;
      }
      this.loadSubmissionSpecs();
      this.searchType = 'newSearch';

      // Auto-execute search if searchById state exists in Vuex
      const saved = this.$store.getters['personSearchModule/getSearchByType']('searchById');
      if (saved && Object.keys(saved).length > 0) {
        this.loadingSavedData = true;
        this.$emit('show', false);
        this.$emit('loadingSavedData', true);
        // Populate fields from saved state
        this.id = saved.id || null;
        this.selectedIdType = saved.idType || 'statePersonId';
        this.personType = saved.personType;
        this.sources = saved.source
          ? saved.source
            .split(',')
            .filter(Boolean)
            .map(code => this.activeSources.find(s => s.code === code) || { code })
          : [];
        await this.$nextTick();
        // Optionally, validate fields if needed
        this.loadingSavedData = false;
        this.$emit('loadingSavedData', false);
        this.search({ saveState: false, pageNum: saved.pageNum || null });
      }
    } catch (err) {
      this.loadingError = true;
      this.messages.push('There were some errors. Please try again. If errors persist please contact support.');
      this.showNotification('danger', this.messages);
    } finally {
      if (this.loadingSavedData) {
        this.loadingSavedData = false;
        this.$emit('loadingSavedData', false);
      }
      this.loading = false;
    }
  },
  computed: {
    ...mapGetters(['userPersonTypes']),
    ...mapGetters('propertiesModule', [
      'properties',
    ]),
    ...mapGetters('lookups', [
      'activeSources',
      'defaultPersonType',
    ]),
    ...mapGetters('fieldLabelsModule', [
      'getFieldLabelByPersonType',
      'getFieldLengthsByPersonType',
      'getFieldRequiredForPersonType',
    ]),
    allSubSpecsArray() {
      return [].concat(...this.allSubmissionSpecs);
    },
    defaultSource() {
      if (this.activeSources) {
        const defaultType = this.activeSources
          .find(el => el.defaultType === true);
        return defaultType ? defaultType.code : this.activeSources[0].code;
      } return null;
    },
    enabledSubmissionStatus() {
      if (this.allSubSpecsArray) {
        return this.allSubSpecsArray
          .filter(d => d.purpose === 'SEARCH' && d.enabled === true)
          .map(p => p.personType.code);
      } return null;
    },
    hasSsn() {
      if (this.hasModule(this.ssnPermission)) {
        return true;
      }
      return false;
    },
    idTypesDisplayed() {
      // Filter idTypes to only display labels user has permission to.
      return this.idTypes.filter(type => !type.permission || this.hasModule(type.permission));
    },
    sortedPersonTypesOptions() {
      const sorted = Array.isArray(this.userPersonTypes)
        ? this.userPersonTypes.map(p => ({
          code: p.code,
          name: p.name,
        }))
        : [];
      if (!this.contextMode) {
        sorted.unshift({ name: 'All', code: null });
      }
      return sorted.slice().sort((a, b) => {
        if (a.name.toUpperCase() < b.name.toUpperCase()) {
          return -1;
        }
        if (a.name.toUpperCase() > b.name.toUpperCase()) {
          return 1;
        }
        return 0;
      });
    },
    contextMode() {
      const identityControl = this.properties['IDENTITY_CONTROL-LIFETIME_IDS'];
      return JSON.parse(identityControl.toString().toLowerCase()) === false;
    },
  },
  methods: {
    ...mapActions('lookups', [
      'loadSources',
      'loadPersonTypes',
    ]),
    checkRequiredFields() {
      if (this.selectedIdType === 'localId') {
        this.validateField('source');
      }
      this.validateField('identifier');
      const err = this.hasErrorIdentifier || this.hasErrorSource;
      if (err) {
        this.showNotification('danger', ['There were some errors. Please fix the errors highlighted in the fields below.']);
      } else {
        this.resetNotifications();
      }
      return err;
    },
    fieldLabel(code) {
      return this.getFieldLabelByPersonType(code, 'ASSIGN', this.personType || this.defaultPersonType);
    },
    fieldLength(code) {
      return this.getFieldLengthsByPersonType(code, 'ASSIGN', this.personType || this.defaultPersonType);
    },
    fieldRequired(code) {
      return this.getFieldRequiredForPersonType(code, 'ASSIGN', this.personType || this.defaultPersonType);
    },
    flatten(obj) {
      const flattenedObject = {};
      this.traverseAndFlatten(obj, flattenedObject);
      return flattenedObject;
    },
    async loadSubmissionSpecs() {
      const personTypeIds = Array.isArray(this.userPersonTypes)
        ? this.userPersonTypes.map(item => item.id)
        : [];
      try {
        this.loading = true;
        this.loadingError = false;
        // build promises to get submission specs info per person type
        const submissionSpecResponse = await Promise.all([...personTypeIds]
          .map(id => axios.get(`person-types/${id}/submission-specs/`)));
        // use the response from above and push them
        submissionSpecResponse.forEach((s) => {
          this.allSubmissionSpecs.push(s.data);
        });
      } catch (err) {
        this.loadingError = true;
      } finally {
        this.loading = false;
      }
    },
    onPasteValidation(fieldName) {
      // Run validation after paste completes
      this.$nextTick(() => {
        this.validateField(fieldName);
      });
    },
    reset() {
      this.id = null;
      this.selectedIdType = 'statePersonId';
      // clear out source from es-select
      if (this.selectedIdType === 'localId') {
        this.$refs.selSource.clear();
      } else {
        this.sources = [];
      }
      // Clear the search state for this searchType in Vuex
      this.$store.dispatch('personSearchModule/resetSearchState', 'searchById');
      this.hasErrorIdentifier = false;
      this.hasErrorSource = false;
      this.msgIdentifier = null;
      this.msgSource = null;
      if (this.userPersonTypesCodes && this.userPersonTypesCodes.length > 0) {
        this.personType = this.userPersonTypesCodes.includes(this.defaultPersonType)
          ? this.defaultPersonType : this.userPersonTypesCodes[0];
      } else {
        this.personType = null;
      }
      this.resetNotifications();
      this.$emit('show', true);
      this.$router.replace({ query: {} }).catch(() => {});
    },
    resetNotifications() {
      this.$store.state.notifications.isActive = false;
      this.$store.state.notifications.notificationType = null;
    },
    search({ saveState = true, pageNum = null } = {}) {
      if (this.checkRequiredFields()) {
        return;
      }

      // remove dash and spaces
      if (this.id != null && this.id !== '') {
        this.id = this.id.trim();
        if (this.selectedIdType === 'statePersonId' || this.selectedIdType === 'ssn') {
          this.id = this.id.replace(/[-\s/]/g, '');
        }
      }

      if (saveState) {
        // Store the search fields under the searchType key in Vuex state
        this.$store.dispatch('personSearchModule/setSearchState', {
          searchType: 'searchById',
          personType: this.personType,
          idType: this.selectedIdType,
          id: this.id,
          source: this.sources.map(source => source.code).toString() || null,
        });
      }

      const sourceCode = this.sources.map(source => source.code).toString();

      const query = {
        idType: this.selectedIdType,
      };

      if (this.personType) {
        query.personType = this.personType;
      }

      if (sourceCode) {
        query.sourceCode = sourceCode;
      }

      if (pageNum) {
        query.page = pageNum;
      }

      this.$router.replace({
        name: 'PidSearchId',
        query,
      });

      this.searchParams();
      this.$emit('show', false);
    },
    searchParams() {
      const params = {};
      params.id = this.id ? this.id : null;
      params.personType = this.personType ? this.personType : null;
      params.idType = this.selectedIdType
        ? this.idTypes.find(e => e.code === this.selectedIdType).code : null;
      if (this.selectedIdType === 'localId') {
        params.sourceCode = this.sources.length ? this.sources[0].code : null;
      }
      this.$emit('searchParams', params);
      return params;
    },
    setDataElements() {
      // Adding permission key for any idTypes that need a permission check.
      this.userPersonTypesCodes = Array.isArray(this.userPersonTypes)
        ? this.userPersonTypes.map(item => item.code)
        : [];
      if (this.userPersonTypesCodes && this.userPersonTypesCodes.length > 0) {
        this.personType = this.userPersonTypesCodes.includes(this.defaultPersonType)
          ? this.defaultPersonType : this.userPersonTypesCodes[0];
      } else {
        this.personType = null;
      }
      this.updateIdTypes();
    },
    traverseAndFlatten(currentNode, target, flattenedKey) {
      Object.keys(currentNode).forEach((key) => {
        if (Object.prototype.hasOwnProperty.call(currentNode, key)) {
          let newKey;
          if (flattenedKey === undefined) {
            newKey = key;
          } else {
            newKey = `${flattenedKey}_${key}`;
          }
          const value = currentNode[key];
          if (typeof value === 'object') {
            this.traverseAndFlatten(value, target, newKey);
          } else {
            target[newKey] = value; // eslint-disable-line no-param-reassign
          }
        }
      });
    },
    updateIdTypes() {
      this.idTypes = [
        {
          code: 'statePersonId',
          description: this.fieldLabel('STATE_STUDENT_ID'),
          label: 'STATE_STUDENT_ID',
        },
        {
          code: 'localId',
          description: this.fieldLabel('LOCAL_STUDENT_ID'),
          label: 'LOCAL_STUDENT_ID',
        },
        {
          code: 'ssn',
          description: this.fieldLabel('SOCIAL_SECURITY_NUMBER'),
          permission: this.ssnPermission,
          label: 'SOCIAL_SECURITY_NUMBER',
        },
      ];
    },
    validateField(field) {
      if (field === 'identifier') {
        // Trim ID if it's a string
        if (typeof this.id === 'string') {
          this.id = this.id.trim();
        }

        const isEmpty = !this.id;

        if (this.selectedIdType === 'ssn') {
          const ssnPattern = /^(?:\d{9}|[Ss]\d{8}|\d{3}-\d{2}-\d{4}|[Ss]\d{2}-\d{2}-\d{4})$/;
          const ssn = isEmpty ? null : this.id;
          const isRequired = this.fieldRequired('SOCIAL_SECURITY_NUMBER');

          if (isRequired && !ssn) {
            this.hasErrorIdentifier = true;
            this.msgIdentifier = `${this.fieldLabel('SOCIAL_SECURITY_NUMBER')} is required.`;
          } else if (ssn && !ssnPattern.test(ssn)) {
            this.hasErrorIdentifier = true;
            this.msgIdentifier = `${this.fieldLabel('SOCIAL_SECURITY_NUMBER')} must be a valid format.`;
          } else {
            this.hasErrorIdentifier = false;
            this.msgIdentifier = null;
          }
        } else {
          const code = this.selectedIdType === 'statePersonId' ? 'STATE_STUDENT_ID' : 'LOCAL_STUDENT_ID';
          const effectiveFieldLength = code === 'LOCAL_STUDENT_ID'
            ? Math.max(this.fieldLength('LOCAL_STUDENT_ID'), this.fieldLength('ALT_STUDENT_ID'))
            : this.fieldLength(code);

          if (isEmpty) {
            this.hasErrorIdentifier = true;
            this.msgIdentifier = `${this.fieldLabel(code)} is required.`;
          } else if (this.id.length > effectiveFieldLength) {
            this.hasErrorIdentifier = true;
            this.msgIdentifier = `${this.fieldLabel(code)} must be less than ${effectiveFieldLength} characters.`;
          } else {
            this.hasErrorIdentifier = false;
            this.msgIdentifier = null;
          }
        }
      }
      if (field === 'source') {
        if (this.fieldRequired('SOURCE_CODE') && this.sources.length === 0) {
          this.hasErrorSource = true;
          this.msgSource = `${this.fieldLabel('SOURCE_CODE')} is required.`;
        } else {
          this.hasErrorSource = false;
          this.msgSource = null;
        }
      }
    },
    checkSsnInput(evt) {
      if (this.selectedIdType === 'ssn') {
        // Listen to the key event when a keyboard key is pressed
        evt = (evt) || window.event; // eslint-disable-line
        const charCode = (evt.which) ? evt.which : evt.keyCode;
        // Prevent user from entering any values except num and (-)
        if (charCode !== 83 && charCode !== 115 && charCode !== 45 && charCode > 31
            && (charCode < 48 || charCode > 83)) {
          evt.preventDefault();
        }
      }
    },
    onIdTypeChange(typeCode) {
      this.selectedIdType = typeCode;
      this.id = null;
      this.sources = [];
      this.hasErrorIdentifier = false;
      this.hasErrorSource = false;
      this.msgIdentifier = null;
      this.msgSource = null;
      this.resetNotifications();
    },
  },
};
</script>

<style lang="sass" scoped>
</style>
